import { addBulkUnits, addUnit, deleteUnit, updateUnassociatedUnit } from '../lib/api';

export const PENDING_ZONE_HEADING = "Assign to a Zone Upon Connection";

export const PENDING_ZONE_HELPER_TEXT =
  "Add this unit to a zone when it connects to the cloud to complete configuration and enable full control";

export const PENDING_ZONE_ASSIGNED_MESSAGE =
  "Unit will be added to the zone when it connects";

export const PENDING_ZONE_REMOVED_MESSAGE =
  "Unit will no longer be added to the zone when it connects";

export const isAwaitingConnection = (unit) => unit?.activated === false;

export const getUnitZone = (unit) => unit?.zone || unit?.pendingZone || null;

const buildDeviceUnitBody = (unit) => {
  const { propertyName, buildingName, ...rest } = unit?.metadata || {};
  return { ...rest, serialNumber: unit?.serialNumber };
};

const buildPendingUnitBody = (unit, zoneUnid = null) => {
  const { propertyName, buildingName, ...rest } = unit?.metadata || {};
  return { ...rest, zoneUnid };
};

export const assignUnitToZone = (unit, zone) =>
  isAwaitingConnection(unit)
    ? updateUnassociatedUnit(
        { serialNumber: unit?.serialNumber },
        buildPendingUnitBody(unit, zone?.unid ?? null)
      )
    : addUnit({ unid: zone?.unid }, buildDeviceUnitBody(unit));

export const assignUnitsToZone = async (units, zone) => {
  const connected = units.filter((unit) => !isAwaitingConnection(unit));
  const awaitingConnection = units.filter(isAwaitingConnection);

  const outcomes = await Promise.allSettled([
    ...(connected.length > 0
      ? [
          addBulkUnits(
            { unid: zone?.unid },
            { units: connected.map((unit) => ({ serialNumber: unit.serialNumber, ...unit.metadata })) }
          ).then(() => connected),
        ]
      : []),
    ...awaitingConnection.map((unit) =>
      updateUnassociatedUnit(
        { serialNumber: unit.serialNumber },
        buildPendingUnitBody(unit, zone?.unid ?? null)
      ).then(() => [unit])
    ),
  ]);

  const assigned = outcomes.flatMap((outcome) =>
    outcome.status === "fulfilled" ? outcome.value : []
  );
  const assignedSerialNumbers = new Set(assigned.map((unit) => unit.serialNumber));

  return {
    assigned,
    failed: units.filter((unit) => !assignedSerialNumbers.has(unit.serialNumber)),
  };
};

export const bySerialNumber = (units) =>
  Object.fromEntries(units.map((unit) => [unit.serialNumber, unit]));

export const clearsPendingZone = (unit) => !unit?.zone;

export const removeUnitFromZone = (unit) =>
  clearsPendingZone(unit)
    ? updateUnassociatedUnit(
        { serialNumber: unit?.serialNumber },
        buildPendingUnitBody(unit, null)
      )
    : deleteUnit({ unid: unit.zone.unid, serialNumber: unit?.serialNumber });

export const removeUnitsFromZone = async (units) => {
  const outcomes = await Promise.allSettled(
    units.map((unit) => removeUnitFromZone(unit).then(() => unit))
  );

  const removed = outcomes.flatMap((outcome) =>
    outcome.status === "fulfilled" ? [outcome.value] : []
  );
  const removedSerialNumbers = new Set(removed.map((unit) => unit.serialNumber));

  return {
    removed,
    failed: units.filter((unit) => !removedSerialNumbers.has(unit.serialNumber)),
  };
};

export const withZoneAssigned = (unit, zone) =>
  isAwaitingConnection(unit) ? { ...unit, pendingZone: zone } : { ...unit, zone };

export const withZoneCleared = (unit) => ({ ...unit, zone: null, pendingZone: null });
